import React, {useEffect} from 'react';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {BoundsRectProvider} from '../../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {QuickInsertCommandDropdownItem} from './quickInsertCommandDropdownItem';
import {useInteractiveListOnCancel} from './quickInsertCommon';
import {QuickInsertCommandSpec, QuickInsertSelectedCommand} from './quickInsertTypes';

/*
 * Props.
 */

interface QuickInsertCommandDropdownProps {
  input: string;
  commands: ReadonlyArray<QuickInsertCommandSpec>;
  editorWrapperRef: React.RefObject<HTMLDivElement>;
  anchor: BoundsRectProvider;
  onCancel: Handler;
  onSelect: HandlerOf<QuickInsertSelectedCommand>;
}

/*
 * Component.
 */

export const QuickInsertCommandDropdown = (props: QuickInsertCommandDropdownProps) => {
  const {input, editorWrapperRef, onCancel} = props;

  const visibleCommands = props.commands.filter((command) => command.name.slice(1).startsWith(input));
  const topCommand = visibleCommands.length ? visibleCommands[0] : null;

  const [onRequestClose, onSelect] = useInteractiveListOnCancel(props.onCancel, props.onSelect);

  useEffect(() => {
    const wrapperElement = editorWrapperRef.current;
    if (!wrapperElement) {
      return undefined;
    }

    const onKeyDownMaybeHandleCommandOrClose = (event: KeyboardEvent) => {
      switch (event.key) {
        // Arrows up and down are trapped by the dropdown
        case 'ArrowLeft':
        case 'ArrowRight':
        case ' ': {
          onCancel();
          break;
        }
        case 'Backspace': {
          if (!input || event.metaKey) {
            onCancel();
          }
          break;
        }
        case 'Enter':
        case 'Tab': {
          if (topCommand) {
            onSelect({command: topCommand.type, input});
          } else {
            onCancel();
          }
          break;
        }
        default:
      }
    };

    wrapperElement.addEventListener('keydown', onKeyDownMaybeHandleCommandOrClose);

    return () => {
      wrapperElement.removeEventListener('keydown', onKeyDownMaybeHandleCommandOrClose);
    };
  });

  return (
    <SimpleDropdown
      anchor={props.anchor}
      maxWidth={300}
      maxHeight={350}
      shouldSelectFirst
      shouldSelectWithTab
      onRequestClose={onRequestClose}
    >
      {visibleCommands.map((command) => (
        <QuickInsertCommandDropdownItem
          key={command.type}
          command={command}
          countMatching={input.length}
          onSelect={() => onSelect({command: command.type, input})}
        />
      ))}
    </SimpleDropdown>
  );
};
